import { ethers } from 'ethers';

const MAX_INDEX_SCAN = 500;

export const REQUEST_STATUS = {
    None: 0,
    Pending: 1,
    Accepted: 2,
    Rejected: 3,
};

export const paymentRefBytes32 = (note = '') => {
    const text = String(note || '').trim();
    if (!text) return ethers.ZeroHash;
    return ethers.id(text.slice(0, 120));
};

export async function listMappingIds(
    readContractData,
    contractAddress,
    contractAbi,
    mappingMethod,
    account,
    max = MAX_INDEX_SCAN,
) {
    const entries = [];
    for (let index = 0; index < max; index += 1) {
        try {
            const raw = await readContractData(
                contractAddress,
                contractAbi,
                mappingMethod,
                [account, index],
            );
            const id = Number(raw);
            if (!Number.isFinite(id) || id <= 0) break;
            entries.push({ id, chainIndex: index });
        } catch (_error) {
            break;
        }
    }
    return entries;
}

export function normalizeDirectPayment(raw) {
    if (!raw) return raw;
    const paymentId = Number(raw.paymentId ?? raw.id);
    const createdAt = Number(raw.createdAt ?? raw.paymentTime ?? 0);
    return {
        ...raw,
        paymentId,
        id: paymentId,
        tokenAddress: raw.tokenAddress ?? raw.token,
        paymentTime: createdAt > 0 && createdAt < 1e12 ? createdAt : createdAt,
        createdAtMs: createdAt >= 1e12 ? createdAt : createdAt * 1000,
        isClaimed: true,
        claimed: true,
    };
}

export function normalizeLockedPayment(raw) {
    if (!raw) return raw;
    const paymentId = Number(raw.paymentId ?? raw.id);
    const disputed = Boolean(raw.isDisputeRaised ?? raw.disputeRaised);
    const finished = Boolean(raw.claimed ?? raw.finalized);
    return {
        ...raw,
        paymentId,
        id: paymentId,
        tokenAddress: raw.tokenAddress ?? raw.token,
        isDisputeRaised: disputed,
        disputeRaised: disputed,
        claimed: finished,
        finalized: finished,
        customSchedule: Boolean(raw.customSchedule),
    };
}

export function normalizePaymentRequest(raw) {
    if (!raw) return raw;
    const requestId = Number(raw.requestId);
    const status = Number(raw.status ?? raw.requestStatus ?? 0);
    const payer = raw.payer ?? raw.receiver;
    const isAccepted =
        status === REQUEST_STATUS.Accepted || Boolean(raw.isAccepted);
    return {
        ...raw,
        requestId,
        payer,
        receiver: payer,
        tokenAddress: raw.tokenAddress ?? raw.token,
        status,
        isAccepted,
    };
}

export function normalizeLockedPaymentRequest(raw) {
    if (!raw) return raw;
    const requestId = Number(raw.requestId);
    const status = Number(raw.status ?? raw.requestStatus ?? 0);
    const payer = raw.payer ?? raw.receiver;
    const isAccepted =
        status === REQUEST_STATUS.Accepted || Boolean(raw.isAccepted);
    return {
        ...raw,
        requestId,
        payer,
        receiver: payer,
        tokenAddress: raw.tokenAddress ?? raw.token,
        status,
        isAccepted,
    };
}

export function normalizeDispute(raw) {
    if (!raw) return null;
    const tuple = Array.isArray(raw)
        ? {
              disputeId: raw[0],
              paymentId: raw[1],
              raisedBy: raw[2],
              resolved: raw[3],
              refundedToSender: raw[4],
              raisedAt: raw[5],
              resolvedAt: raw[6],
              status: raw[7],
          }
        : raw;
    return {
        ...tuple,
        refunded: Boolean(tuple.refundedToSender),
        refundedToSender: Boolean(tuple.refundedToSender),
    };
}

/**
 * Locked-payment escrow snapshot for admin dispute split resolution.
 */
export async function fetchPaymentDisputeEscrowContext(
    readContractData,
    contractAddress,
    contractAbi,
    paymentId,
    erc20Abi,
) {
    if (paymentId == null || paymentId === '') return null;

    const pid = BigInt(paymentId);
    const raw = await readContractData(
        contractAddress,
        contractAbi,
        'lockedPayments',
        [pid],
    );
    const payment = normalizeLockedPayment(raw);
    if (!payment) return null;

    let remainingWei;
    try {
        remainingWei = await readContractData(
            contractAddress,
            contractAbi,
            'getLockedPaymentRemaining',
            [pid],
        );
    } catch (_error) {
        const total = BigInt(payment.totalAmount ?? 0);
        const claimed = BigInt(payment.claimedAmount ?? 0);
        remainingWei = total - claimed;
    }

    const tokenAddress = payment.token || payment.tokenAddress;
    let decimals = 6;
    if (tokenAddress && erc20Abi) {
        try {
            const rawDecimals = await readContractData(
                tokenAddress,
                erc20Abi,
                'decimals',
                [],
            );
            decimals = Number(rawDecimals);
            if (!Number.isFinite(decimals)) decimals = 6;
        } catch (_error) {
            decimals = 6;
        }
    }

    const remaining = BigInt(remainingWei ?? 0);
    const total = BigInt(payment.totalAmount ?? 0);
    const claimed = BigInt(payment.claimedAmount ?? 0);

    return {
        paymentId: Number(payment.paymentId ?? paymentId),
        sender: payment.sender,
        receiver: payment.receiver,
        tokenAddress,
        totalAmountWei: total.toString(),
        claimedAmountWei: claimed.toString(),
        remainingWei: remaining.toString(),
        decimals,
        totalFormatted: ethers.formatUnits(total, decimals),
        claimedFormatted: ethers.formatUnits(claimed, decimals),
        remainingFormatted: ethers.formatUnits(remaining, decimals),
        disputeRaised: Boolean(payment.disputeRaised ?? payment.isDisputeRaised),
    };
}

export async function fetchDirectPayments(
    readContractData,
    contractAddress,
    contractAbi,
    walletAddress,
    role,
) {
    const mapping =
        role === 'sent' ? 'directPaymentsBySender' : 'directPaymentsByReceiver';
    const entries = await listMappingIds(
        readContractData,
        contractAddress,
        contractAbi,
        mapping,
        walletAddress,
    );
    const rows = [];
    for (const { id, chainIndex } of entries) {
        const raw = await readContractData(
            contractAddress,
            contractAbi,
            'directPayments',
            [id],
        );
        rows.push({ ...normalizeDirectPayment(raw), chainIndex });
    }
    return rows;
}

export async function fetchLockedPayments(
    readContractData,
    contractAddress,
    contractAbi,
    walletAddress,
    role,
) {
    const mapping =
        role === 'sent' ? 'lockedPaymentsBySender' : 'lockedPaymentsByReceiver';
    const entries = await listMappingIds(
        readContractData,
        contractAddress,
        contractAbi,
        mapping,
        walletAddress,
    );
    const rows = [];
    for (const { id, chainIndex } of entries) {
        const raw = await readContractData(
            contractAddress,
            contractAbi,
            'lockedPayments',
            [id],
        );
        rows.push({ ...normalizeLockedPayment(raw), chainIndex });
    }
    return rows;
}

/** Fetch locked payments by known on-chain ids (DB-first claims path). */
export async function fetchLockedPaymentsByIds(
    readContractData,
    contractAddress,
    contractAbi,
    paymentIds = [],
) {
    const rows = [];
    const seen = new Set();
    for (const rawId of paymentIds) {
        const id = Number(rawId);
        if (!Number.isFinite(id) || id <= 0 || seen.has(id)) continue;
        seen.add(id);
        try {
            const raw = await readContractData(
                contractAddress,
                contractAbi,
                'lockedPayments',
                [id],
            );
            const normalized = normalizeLockedPayment({
                ...raw,
                paymentId: raw?.paymentId ?? id,
            });
            if (!normalized?.paymentId && !normalized?.receiver) continue;
            rows.push({ ...normalized, paymentId: id, id });
        } catch (_error) {
            // skip missing ids
        }
    }
    return rows;
}

export async function fetchPaymentRequests(
    readContractData,
    contractAddress,
    contractAbi,
    walletAddress,
    role,
) {
    const mapping =
        role === 'sent'
            ? 'paymentRequestsByRequester'
            : 'paymentRequestsByPayer';
    const entries = await listMappingIds(
        readContractData,
        contractAddress,
        contractAbi,
        mapping,
        walletAddress,
    );
    const rows = [];
    for (const { id, chainIndex } of entries) {
        const raw = await readContractData(
            contractAddress,
            contractAbi,
            'paymentRequests',
            [id],
        );
        rows.push({ ...normalizePaymentRequest(raw), chainIndex });
    }
    return rows;
}

export async function fetchLockedPaymentRequests(
    readContractData,
    contractAddress,
    contractAbi,
    walletAddress,
    role,
) {
    const mapping =
        role === 'sent'
            ? 'lockedPaymentRequestsByRequester'
            : 'lockedPaymentRequestsByPayer';
    const entries = await listMappingIds(
        readContractData,
        contractAddress,
        contractAbi,
        mapping,
        walletAddress,
    );
    const rows = [];
    for (const { id, chainIndex } of entries) {
        const raw = await readContractData(
            contractAddress,
            contractAbi,
            'lockedPaymentRequests',
            [id],
        );
        rows.push({ ...normalizeLockedPaymentRequest(raw), chainIndex });
    }
    return rows;
}

export async function countMappingEntries(
    readContractData,
    contractAddress,
    contractAbi,
    mappingMethod,
    account,
) {
    const entries = await listMappingIds(
        readContractData,
        contractAddress,
        contractAbi,
        mappingMethod,
        account,
    );
    return entries.length;
}

export async function fetchDisputesForAccount(
    readContractData,
    contractAddress,
    contractAbi,
    walletAddress,
) {
    const entries = await listMappingIds(
        readContractData,
        contractAddress,
        contractAbi,
        'disputesByAccount',
        walletAddress,
    );
    const rows = [];
    for (const { id: disputeId, chainIndex } of entries) {
        const raw = await readContractData(
            contractAddress,
            contractAbi,
            'disputes',
            [disputeId],
        );
        const normalized = normalizeDispute(raw);
        if (normalized) {
            rows.push({ ...normalized, chainIndex });
        }
    }
    return rows;
}

export async function resolveDisputeIdForPayment(
    readContractData,
    contractAddress,
    contractAbi,
    paymentId,
) {
    try {
        const disputeId = await readContractData(
            contractAddress,
            contractAbi,
            'getDisputeIdForPayment',
            [paymentId],
        );
        const numeric = Number(disputeId);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    } catch (_error) {
        try {
            const disputeId = await readContractData(
                contractAddress,
                contractAbi,
                'paymentToDispute',
                [paymentId],
            );
            const numeric = Number(disputeId);
            return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
        } catch (_inner) {
            return null;
        }
    }
}

export const DIRECT_PAYMENT_CREATED_EVENTS = [
    'DirectPaymentCreated',
    'DirectPaymentInitiated',
];

export const LOCKED_PAYMENT_CREATED_EVENTS = [
    'LockedPaymentCreated',
    'CustomLockedPaymentCreated',
    'LockedPaymentInitiated',
];

export const PAYMENT_REQUEST_RESOLVED_EVENTS = [
    'PaymentRequestResolved',
    'PaymentRequestAccepted',
];

export const LOCKED_PAYMENT_REQUEST_RESOLVED_EVENTS = [
    'LockedPaymentRequestResolved',
    'LockedPaymentRequestAccepted',
];

export function decodePaymentCreatedId(decoded) {
    if (!decoded?.eventName) return null;
    if (!DIRECT_PAYMENT_CREATED_EVENTS.includes(decoded.eventName)) return null;
    const id = decoded.args?.paymentId;
    return id != null ? String(id) : null;
}

export function decodeLockedPaymentCreatedId(decoded) {
    if (!decoded?.eventName) return null;
    if (!LOCKED_PAYMENT_CREATED_EVENTS.includes(decoded.eventName)) return null;
    const id = decoded.args?.paymentId;
    return id != null ? String(id) : null;
}

const normalizeAdminAddress = (value) => {
    const address = String(value || '').trim();
    if (!address || address === ethers.ZeroAddress) return null;
    return address;
};

export async function resolveCpgAdminAddresses(
    readContractData,
    contractAddress,
    contractAbi,
) {
    const read = async (functionName) => {
        try {
            return await readContractData(
                contractAddress,
                contractAbi,
                functionName,
                [],
            );
        } catch (_error) {
            return null;
        }
    };

    const disputeAdmin = normalizeAdminAddress(await read('disputeAdmin'));
    const owner = normalizeAdminAddress(await read('owner'));

    return {
        disputeAdmin,
        owner,
        primary: disputeAdmin || owner,
    };
}

export async function fetchLockedEscrowFeeBps(
    readContractData,
    contractAddress,
    contractAbi,
) {
    try {
        const bps = await readContractData(
            contractAddress,
            contractAbi,
            'feeBpsLockedEscrow',
            [],
        );
        return Number(bps) || 0;
    } catch (_error) {
        return 0;
    }
}

export async function fetchLockedPaymentSchedule(
    readContractData,
    contractAddress,
    contractAbi,
    paymentId,
) {
    try {
        const length = await readContractData(
            contractAddress,
            contractAbi,
            'getLockedPaymentScheduleLength',
            [paymentId],
        );
        const total = Number(length);
        if (!total) return [];

        const slots = [];
        for (let index = 0; index < total; index += 1) {
            const raw = await readContractData(
                contractAddress,
                contractAbi,
                'getLockedPaymentMilestone',
                [paymentId, index],
            );
            const unlockAt = Number(raw?.unlockAt ?? raw?.[0] ?? 0);
            const amount = raw?.amount ?? raw?.[1];
            slots.push({
                index,
                unlockAt,
                amount: amount?.toString?.() ?? String(amount ?? '0'),
            });
        }
        return slots;
    } catch (_error) {
        return [];
    }
}

export function isWalletCpgAdmin(walletAddress, adminAddresses) {
    const wallet = String(walletAddress || '').trim().toLowerCase();
    if (!wallet || !adminAddresses) return false;

    const candidates = [adminAddresses.disputeAdmin, adminAddresses.owner]
        .filter(Boolean)
        .map((address) => String(address).toLowerCase());

    return candidates.includes(wallet);
}
